/**
 * Control autoplaying a video when it comes into view the first time and pausing it automatically when it scrolls
 * off screen.
 *
 * @param autoplayElement - The element containing the autoplay data attribute, should have a video tag as a descendant
 *  element.
 * @param isVisible - Whether or not the element is visible.
 * @param deactivateObserver - Function called to stop tracking the visibility of the video.
 * @param activateObserver - Function called to start tracking the visibility of the video.
 */
let videoAutoPlay = (autoplayElement, isVisible, deactivateObserver, activateObserver) => {
    // Grab the first descendant video tag
    let video = autoplayElement.querySelector('video');
    if (isVisible) {
        // If the video has finished playing don't restart it
        // and only start playing again if we are currently paused
        if (!video.ended && video.paused) {
            video.muted = true;
            video.play();
        }
    } else {
        video.pause();
    }

    // After the video is paused (caused either by scrolling the video off screen or the user pausing)
    // deactivate the listener so that if the video gets scrolled back into view later we don't start playing again.
    video.addEventListener('pause', () => deactivateObserver());
    // When the video is played we want to start tracking it again so that when it goes off screen we can
    // pause the video.
    video.addEventListener('play', () => activateObserver());
};

/**
 * Fire trackVisibleTag whenever a targeted element scrolls into view.
 * @type {IntersectionObserver}
 */
const intersectionObserver = new IntersectionObserver(
    (observedEntries, observer) => {
        observedEntries
            .forEach((entry) =>
                videoAutoPlay(
                    entry.target,
                    entry.isIntersecting,
                    () => observer.unobserve(entry.target),
                    () => observer.observe(entry.target)
                )
            )
    }, {
        threshold: [0.5,]
    }
);

const SELECTOR = '[data-anvil-autoplay]';
/**
 * Connect the intersectionObserver to elements as they are added to the DOM.
 * @type {MutationObserver}
 */
const mutationObserver = new MutationObserver((mutations) => {
    mutations
        .filter((mutation) => mutation.type === 'childList')
        .forEach((mutation) => {
            Array.from(mutation.addedNodes)
                .filter((node) => node.nodeType === Node.ELEMENT_NODE)
                .forEach((node) => {
                    if (node.matches(SELECTOR))
                        intersectionObserver.observe(node);
                    for (let el of node.querySelectorAll(SELECTOR))
                        intersectionObserver.observe(el);
                })
        });
});

/**
 * Set up intersectionObserver for elements that are already on the page and activate mutationObserver for elements
 * that may be added later.
 */
window.addEventListener('DOMContentLoaded', () => {
    for (let el of document.querySelectorAll(SELECTOR))
        intersectionObserver.observe(el);
    mutationObserver.observe(document.body, {subtree: true, childList: true,});
});
